import { useCallback, useEffect } from 'react';
import { atom, useAtom } from 'jotai';
import { useLatest } from 'ahooks';
import { useUserLoggedIn } from '~/modules/user';
import type { Conversation, DirectConversation } from '../types';

type UpdatableConversationProperties = Pick<
    DirectConversation,
    | 'UnreadMessageCount'
    | 'IsImportant'
    | 'IsArchived'
    | 'IsDone'
    | 'IsMuted'
    | 'Name'
    | 'BuildHub'
    | 'Thumbnail'
    | 'Participants'
> & {
    Messages?: DirectConversation['Messages']['nodes'];
};

interface CachedConversation {
    /**
     * The cached conversation details.
     */
    conversation: Conversation;
    /**
     * The professional ID if this conversation was opened from the company inbox
     * and is being viewed from the perspective of the professional. If `null`,
     * the personal inbox context will be used.
     */
    professionalID: number | null;
}

/**
 * The maximum number of conversations to cache. Make sure this is always larger
 * than the maximum number of open chat boxes.
 */
const MAX_CACHED_CONVERSATIONS = 50;

/**
 * The list of conversations that have been cached, ordered from newest to oldest.
 */
const conversationCacheAtom = atom<CachedConversation[]>([]);
/**
 * The latest cached conversation.
 */
const lastCachedConversationAtom = atom<CachedConversation | null>(null);

/**
 * Hook to manage the conversation cache.
 */
export function useConversationCache() {
    const { isLoggedIn } = useUserLoggedIn();
    const [conversationCache, setConversationCache] = useAtom(
        conversationCacheAtom,
    );
    const [lastCachedConversation, setLastCachedConversation] = useAtom(
        lastCachedConversationAtom,
    );

    const getCachedConversation = useCallback(
        (conversationID: number, professionalID: number | null) => {
            return conversationCache.find(
                (cached) =>
                    cached.conversation.ID === String(conversationID) &&
                    cached.professionalID === professionalID,
            )?.conversation;
        },
        [conversationCache],
    );
    // Use latest ref to be passed to dependency array to prevent unnecessary re-renders.
    const getCachedConversationRef = useLatest(getCachedConversation);

    const getCachedConversationByParticipants = useCallback(
        (
            userID: number,
            memberIDs: number[],
            professionalIDs: number[] = [],
        ) => {
            if (memberIDs.length === 0 && professionalIDs.length === 0) {
                return undefined;
            }
            const conversations = conversationCache
                // TODO: Assume all personal inbox context for now.
                .filter((cached) => cached.professionalID === null)
                .map((cached) => cached.conversation)
                .filter(
                    (c): c is DirectConversation =>
                        c.__typename === 'DirectConversation' &&
                        c.Participants.length ===
                            memberIDs.length + professionalIDs.length + 1 &&
                        c.Participants.some(
                            ({ Identity }) =>
                                Identity.__typename === 'Member' &&
                                Identity.ID === userID,
                        ) &&
                        memberIDs.every((memberID) =>
                            c.Participants.some(
                                ({ Identity }) =>
                                    Identity.__typename === 'Member' &&
                                    Identity.ID === memberID,
                            ),
                        ) &&
                        professionalIDs.every((professionalID) =>
                            c.Participants.some(
                                ({ Identity }) =>
                                    Identity.__typename === 'Professional' &&
                                    Identity.ID === professionalID,
                            ),
                        ),
                );
            if (conversations.length <= 1) {
                return conversations[0];
            }
            // If more than one exists, first return the non-group chat one.
            const nonGroupChat = conversations.find((c) => !c.IsGroupChat);
            if (nonGroupChat) {
                return nonGroupChat;
            }
            // If more than one group chat exists, return the one with the latest message.
            conversations.sort((a, b) => {
                const aLatestMessage = new Date(
                    a.Messages.nodes[0]?.Created ?? 0,
                ).getTime();
                const bLatestMessage = new Date(
                    b.Messages.nodes[0]?.Created ?? 0,
                ).getTime();
                return bLatestMessage - aLatestMessage;
            });
            return conversations[0];
        },
        [conversationCache],
    );

    const cacheConversation = useCallback(
        (conversation: Conversation, professionalID: number | null) => {
            setConversationCache((prev) => {
                return [
                    { conversation, professionalID },
                    ...prev.filter(
                        (cached) =>
                            cached.conversation.ID !==
                                String(conversation.ID) ||
                            cached.professionalID !== professionalID,
                    ),
                ].slice(0, MAX_CACHED_CONVERSATIONS);
            });
            setLastCachedConversation({ conversation, professionalID });
        },
        [setConversationCache, setLastCachedConversation],
    );

    const updateCachedConversation = useCallback(
        (
            conversationID: number,
            professionalID: number | null,
            updates: Partial<UpdatableConversationProperties>,
        ) => {
            const cachedConversation = getCachedConversationRef.current(
                conversationID,
                professionalID,
            );
            if (!cachedConversation) {
                return;
            }
            // Only update fields that are not `undefined`.
            const updatedConversation: Conversation = {
                ...cachedConversation,
                ...(updates.UnreadMessageCount !== undefined
                    ? { UnreadMessageCount: updates.UnreadMessageCount }
                    : {}),
                ...(updates.IsImportant !== undefined
                    ? { IsImportant: updates.IsImportant }
                    : {}),
                ...(updates.IsArchived !== undefined
                    ? { IsArchived: updates.IsArchived }
                    : {}),
                ...(updates.IsDone !== undefined
                    ? { IsDone: updates.IsDone }
                    : {}),
                ...(updates.IsMuted !== undefined
                    ? { IsMuted: updates.IsMuted }
                    : {}),
                ...(updates.Name !== undefined ? { Name: updates.Name } : {}),
                ...(updates.BuildHub !== undefined
                    ? { BuildHub: updates.BuildHub }
                    : {}),
                ...(updates.Thumbnail !== undefined
                    ? { Thumbnail: updates.Thumbnail }
                    : {}),
            };
            if (updatedConversation.__typename === 'DirectConversation') {
                // Append new messages to the cached messages.
                const cachedMessages = updates.Messages?.length
                    ? updatedConversation.Messages.nodes
                    : null;
                if (cachedMessages && updates.Messages) {
                    for (const message of updates.Messages) {
                        if (!cachedMessages.some((m) => m.ID === message.ID)) {
                            cachedMessages.unshift(message);
                        }
                    }
                }
                cacheConversation(
                    {
                        ...updatedConversation,
                        ...(updates.Participants !== undefined
                            ? { Participants: updates.Participants }
                            : {}),
                        ...(cachedMessages?.length
                            ? {
                                  Messages: {
                                      ...updatedConversation.Messages,
                                      nodes: cachedMessages,
                                  },
                              }
                            : {}),
                    },
                    professionalID,
                );
            } else {
                cacheConversation(updatedConversation, professionalID);
            }
        },
        [getCachedConversationRef, cacheConversation],
    );

    const replaceCachedConversationMessages = useCallback(
        (
            conversationID: number,
            professionalID: number | null,
            messages: DirectConversation['Messages']['nodes'],
        ) => {
            const cachedConversation = getCachedConversationRef.current(
                conversationID,
                professionalID,
            );
            if (!cachedConversation) {
                return;
            }
            if (cachedConversation.__typename === 'DirectConversation') {
                cacheConversation(
                    {
                        ...cachedConversation,
                        Messages: {
                            ...cachedConversation.Messages,
                            nodes: messages,
                        },
                    },
                    professionalID,
                );
            } else {
                console.warn(
                    'updateCachedConversationMessages: Not a direct conversation',
                );
            }
        },
        [getCachedConversationRef, cacheConversation],
    );

    const invalidateCachedConversation = useCallback(
        (conversationID: number, professionalID: number | null) => {
            setConversationCache((prev) => {
                // Conditionally set new value to avoid unnecessary re-renders.
                if (
                    prev.some(
                        (cached) =>
                            cached.conversation.ID === String(conversationID) &&
                            cached.professionalID === professionalID,
                    )
                ) {
                    return prev.filter(
                        (cached) =>
                            cached.conversation.ID !== String(conversationID) ||
                            cached.professionalID !== professionalID,
                    );
                }
                return prev;
            });
            setLastCachedConversation((prev) => {
                if (
                    prev?.conversation.ID === String(conversationID) &&
                    prev.professionalID === professionalID
                ) {
                    return null;
                }
                return prev;
            });
        },
        [setConversationCache, setLastCachedConversation],
    );

    // Clear the conversation cache when the user logs out.
    useEffect(() => {
        if (!isLoggedIn) {
            setConversationCache([]);
            setLastCachedConversation(null);
        }
    }, [isLoggedIn, setConversationCache, setLastCachedConversation]);

    return {
        /**
         * The latest cached conversation.
         */
        lastCachedConversation,
        /**
         * Get the cached conversation by the conversation ID.
         */
        getCachedConversation,
        /**
         * Get the cached conversation by the participants.
         */
        getCachedConversationByParticipants,
        /**
         * Cache a conversation. If the number of cached conversations exceeds the
         * maximum, the oldest conversation will be removed.
         * @param conversation The conversation to cache.
         * @param professionalID The professional ID if in a company inbox context.
         */
        cacheConversation,
        /**
         * Update a subset of fields of a cached (direct) conversation, if the
         * conversation is already cached. If conversation messages are passed,
         * they will be appended to the current cached conversation messages
         * (not replaced).
         */
        updateCachedConversation,
        /**
         * Replace the messages of a cached (direct) conversation, if the
         * conversation is already cached.
         */
        replaceCachedConversationMessages,
        /**
         * Invalidate a cached conversation.
         */
        invalidateCachedConversation,
    };
}
